import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { dropdownSpalten, gepruefteSpalten, readFormats } from "@/office/excel/formate";
import { nichtPruefbar } from "../schnittstelle";

export const lektionDropdowns: Lesson = {
  id: "dropdowns",
  title: "Dropdowns: Auswählen statt Tippen",
  summary: "Eine Liste zum Anklicken verhindert genau die Tippfehler, die später keine Auswertung mehr findet.",
  durationMinutes: 12,
  learningGoals: [
    "Eine Dropdownliste als Datenüberprüfung anlegen",
    "Die Auswahlwerte in einen benannten Bereich auslagern",
    "Erklären, warum ein Dropdown mehr ist als eine Eingabehilfe",
  ],
  steps: [
    {
      id: "warum-dropdown",
      title: "Warum getippte Kategorien scheitern",
      explanation: [
        "In der Spalte „Region“ steht Nord, nord, NORD, „Nord “ mit Leerzeichen und " +
          "einmal Nrod. Für einen Menschen ist das fünfmal dasselbe. Für Excel sind es " +
          "mindestens drei verschiedene Werte – eine PivotTable macht daraus drei " +
          "Zeilen, ein SUMMEWENN findet nur einen Teil, und ein Filter zeigt eine " +
          "Liste, in der man scrollen muss.",
        "Das Problem entsteht nicht beim Auswerten, sondern beim Eintippen, und dort " +
          "muss es gelöst werden. Eine Datenüberprüfung mit Liste macht aus dem " +
          "Freitextfeld eine Auswahl: Excel zeigt einen Pfeil in der Zelle, man klickt " +
          "den gewünschten Wert an, und ein anderer Wert wird gar nicht erst " +
          "angenommen.",
        "Der Nutzen reicht über die Sauberkeit hinaus. Ein Dropdown ist auch eine " +
          "Dokumentation: Es sagt jedem, der die Datei zum ersten Mal öffnet, welche " +
          "Werte hier überhaupt vorgesehen sind. Ohne Dropdown muss man raten oder " +
          "nach unten scrollen und nachsehen, was andere eingetragen haben.",
        "Zu finden ist die Einstellung unter „Daten“ → „Datenüberprüfung“. Der Dialog " +
          "hat drei Registerkarten: „Einstellungen“ für die Regel selbst, " +
          "„Eingabemeldung“ für einen Hinweis, der beim Anklicken erscheint, und " +
          "„Fehlermeldung“ für den Text, wenn jemand etwas Unzulässiges eingibt.",
      ],
      keyPoints: [
        "Getippte Kategorien werden zu drei Varianten desselben Werts.",
        "Daten → Datenüberprüfung → Zulassen: Liste.",
        "Ein Dropdown dokumentiert nebenbei, welche Werte vorgesehen sind.",
      ],
      task: {
        instruction:
          "Lege für eine deiner Kategoriespalten – etwa Region oder Produkt – eine " +
          "Dropdownliste an.",
        hint: "Spalte markieren, Daten → Datenüberprüfung → Zulassen: Liste. Als Quelle die Werte mit Semikolon getrennt oder einen Bereich.",
        check: async () => {
          const formate = await readFormats();
          if (formate.validations === null) {
            return nichtPruefbar(
              "Die Datenüberprüfung eines Bereichs",
              "1.8",
              "Klicke eine Zelle der Spalte an – erscheint rechts ein Auswahlpfeil, ist das Dropdown da.",
            );
          }

          const listen = dropdownSpalten(formate);
          if (listen.length === 0) {
            const andere = gepruefteSpalten(formate);
            return fail(
              andere.length > 0
                ? `Es gibt Datenüberprüfungen (${[...new Set(andere.map((eintrag) => eintrag.type))].join(", ")}), ` +
                  "aber noch keine Liste."
                : "In der Arbeitsmappe ist noch keine Datenüberprüfung hinterlegt.",
              "Markiere eine Kategoriespalte und wähle Daten → Datenüberprüfung → Zulassen: Liste.",
            );
          }

          const erste = listen[0];
          return pass(
            `Auf „${erste.sheetName}“ trägt der Bereich ${erste.address} eine Dropdownliste` +
              (erste.listSource ? ` mit der Quelle „${erste.listSource}“` : "") +
              ". Tippfehler kommen dort nicht mehr hinein.",
          );
        },
      },
    },
    {
      id: "quelle-auslagern",
      title: "Die Auswahlwerte gehören auf ein Blatt",
      explanation: [
        "Im Feld „Quelle“ lassen sich die Werte direkt eintippen, mit Semikolon " +
          "getrennt: Nord;Süd;Ost;West. Das ist schnell und hat denselben Nachteil wie " +
          "jede getippte Konstante – die Liste steckt unsichtbar in einer Einstellung. " +
          "Kommt eine fünfte Region dazu, muss man erst einmal wissen, wo man suchen " +
          "muss.",
        "Der bessere Weg: Die Werte stehen als Spalte auf einem Blatt " +
          "„Einstellungen“, und die Quelle verweist darauf. Dann sieht jeder, welche " +
          "Werte gelten, und eine Ergänzung ist eine neue Zeile statt ein Dialog.",
        "Damit die Liste beim Wachsen mitkommt, gibt es zwei Wege. Entweder man macht " +
          "aus den Werten eine Tabelle im Sinne von Modul 5 und verweist über einen " +
          "benannten Bereich darauf – das Thema des nächsten Moduls. Oder man " +
          "markiert von vornherein mehr Zeilen als nötig; leere Zellen im Quellbereich " +
          "erscheinen nicht im Dropdown.",
        "Eine Eigenheit älterer Excel-Versionen: Die Quelle muss auf demselben Blatt " +
          "liegen wie das Dropdown – es sei denn, man verweist über einen benannten " +
          "Bereich. Das ist einer der Gründe, warum es benannte Bereiche gibt, und " +
          "gleich das Thema des nächsten Moduls.",
      ],
      keyPoints: [
        "Werte auf ein Blatt „Einstellungen“ statt in das Quellfeld tippen.",
        "Leere Zellen im Quellbereich erscheinen nicht im Dropdown.",
        "Ein benannter Bereich als Quelle funktioniert auch blattübergreifend.",
      ],
      task: {
        instruction:
          "Ändere die Quelle deines Dropdowns so, dass sie auf einen Zellbereich " +
          "verweist, statt die Werte im Dialog aufzuzählen.",
        hint: "Werte in eine Spalte schreiben, dann im Feld „Quelle“ das Symbol rechts anklicken und den Bereich markieren.",
        check: async () => {
          const formate = await readFormats();
          if (formate.validations === null) {
            return nichtPruefbar(
              "Die Datenüberprüfung eines Bereichs",
              "1.8",
              "Öffne Daten → Datenüberprüfung und sieh nach, ob im Feld „Quelle“ ein Bereichsbezug steht.",
            );
          }

          const listen = dropdownSpalten(formate);
          if (listen.length === 0) {
            return fail(
              "In der Arbeitsmappe gibt es noch keine Dropdownliste.",
              "Löse zuerst die Aufgabe im vorigen Schritt.",
            );
          }

          // Eine Quelle mit Bezug beginnt mit "=" - eine aufgezählte Liste nicht.
          const mitBezug = listen.filter(
            (eintrag) => eintrag.listSource !== null && eintrag.listSource.trim().startsWith("="),
          );

          if (mitBezug.length === 0) {
            const erste = listen[0];
            return fail(
              `Die Quelle des Dropdowns auf „${erste.sheetName}“ lautet ` +
                `„${erste.listSource ?? "unbekannt"}“ – die Werte sind im Dialog aufgezählt und ` +
                "damit unsichtbar auf dem Blatt.",
              "Schreibe die Werte in eine Spalte, am besten auf ein Blatt „Einstellungen“, und trage " +
                "im Feld „Quelle“ einen Verweis auf diesen Bereich ein.",
            );
          }

          const erste = mitBezug[0];
          return pass(
            `Die Quelle lautet „${erste.listSource}“ – die zulässigen Werte stehen sichtbar auf einem ` +
              "Blatt und lassen sich ergänzen, ohne einen Dialog zu öffnen.",
          );
        },
      },
    },
  ],
};
